import { useCallback, useEffect, useRef, useState } from "react";
import { isLoggedIn } from "api/coachAuth";
import { flushSync } from "react-dom";
import {
  getLastShownIndex,
  hasBlockUserField,
  isBlockUserFieldFilled,
} from "logic/logics";
import { usePrevious } from "hooks/usePrevious";
import * as _ from "lodash";
import Block from "components/task/content/block/Block";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { ProgramContentType } from "data/BlockComponent";
import { isMobile } from "react-device-detect";
import { deviceAtom } from "recoil/deviceAtom";
import { layoutAtom } from "recoil/layoutAtom";
import { CircularProgress, Sheet, Stack } from "@mui/joy";
import CustomModal from "../../common/CustomModal";
import { useTranslation } from "react-i18next";
import useSaveContentData from "hooks/useSaveContentData";
import useGetThoughtRecordData from "../../../hooks/useGetThoughtRecordData";
import { invalidTextareaIndexAtom } from "../../../recoil/invalidTextareaIndexAtom";
import useDetectIOS from "../../../hooks/useDetectIOS";

export type TaskContentViewerMode = "auto" | "patient" | "coach";

export default function TaskContent({
  taskKey,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  data,
  setData,
  moveToIndex,
  setMoveToIndex,
  isDone,
  isOpen,
  translationVersion,
  viewerMode = "auto",
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  data?: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  moveToIndex?: number;
  setMoveToIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  isDone?: boolean;
  isOpen?: boolean;
  translationVersion?: string;
  viewerMode?: TaskContentViewerMode;
}) {
  const { isFetching } = useGetThoughtRecordData({
    trk: thoughtRecordKey,
    isFirst: false,
    enabled: !!thoughtRecordKey,
  });

  //변경사항 있을 때 저장
  useSaveContentData({
    taskKey,
    data,
    thoughtRecordKey,
    meditationRecordKey,
    translationVersion,
    isFetching,
    isDone,
  });
  const lastIndex = getLastShownIndex(data);

  const [currentIndex, setCurrentIndex] = useState<number>(lastIndex);

  const user = useRecoilValue(userAtom);
  const isCoach =
    viewerMode === "auto"
      ? isLoggedIn(user) && user?.role !== "patient"
      : viewerMode === "coach";
  const isReadOnlyTask = taskKey.includes("0-3-W");

  const device = useRecoilValue(deviceAtom);

  const { isIOS } = useDetectIOS();

  const { t: t_ui } = useTranslation("translation", { keyPrefix: "ui" });

  const [readOnlyModalOpen, setReadOnlyModalOpen] = useState(false);

  const handleAttemptEdit = () => {
    if (isDone && !isCoach && !isReadOnlyTask) {
      setReadOnlyModalOpen(true);
    }
  };

  const focusElement = (el: HTMLElement) => {
    el.focus();
    try {
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      const len = input.value?.length || 0;
      input.setSelectionRange?.(len, len);
    } catch {}

    if (isIOS) {
      try {
        el.click();
      } catch {}
    }
  };

  const focusBlockInput = (index: number) => {
    if (isCoach) return;
    const blockEl = blockRefs.current[index];
    if (!blockEl) return;
    const focusables = Array.from(
      blockEl.querySelectorAll<HTMLElement>(
        'textarea, input[type="text"], input[type="number"], input[type="tel"], .ql-editor'
      )
    );
    const inputEl = focusables.find(
      (el) =>
        !el.closest("[data-feedback-cell]") &&
        !el.closest("[data-disable-autofocus]")
    );
    if (inputEl) {
      focusElement(inputEl);
    }
  };

  const { isTaskContainerSmall } = useRecoilValue(layoutAtom);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>();

  const smoothScrollTo = useCallback((index: number) => {
    const blockEl = blockRefs.current[index];
    if (!blockEl) return;

    blockEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const previousData = usePrevious(data);

  const blockRefs = useRef<HTMLDivElement[]>([]);

  const resetInvalidTextarea = useResetRecoilState(invalidTextareaIndexAtom);

  // Skip the first auto-scroll that happens due to initial data/comment hydration
  const skipNextAutoScrollRef = useRef<boolean>(true);

  useEffect(() => {
    resetInvalidTextarea();
    return () => {
      resetInvalidTextarea();
    };
  }, [taskKey, thoughtRecordKey, activityRecordKey, meditationRecordKey]);

  // Reset the skip flag whenever task/record keys change (opening a task)
  useEffect(() => {
    skipNextAutoScrollRef.current = true;
  }, [taskKey, thoughtRecordKey, activityRecordKey, meditationRecordKey]);

  useEffect(() => {
    if (data && data.length > 0) {
      if (previousData) {
        const changedIndex = data.findIndex(
          (element, index) => !_.isEqual(element, previousData[index])
        );
        if (changedIndex > -1) {
          setCurrentIndex(changedIndex);
        }
        //조건에 따른 블록
        if (
          changedIndex > -1 &&
          hasBlockUserField(data[changedIndex]) &&
          isBlockUserFieldFilled(data[changedIndex]) &&
          !data[changedIndex].hideIfPatient &&
          ((data[changedIndex].nextBlockId &&
            data[changedIndex].nextBlockId !==
              (previousData[changedIndex] as ProgramContentType).nextBlockId) ||
            data[changedIndex].autoOpenNext)
        ) {
          console.log(
            "[TEST]",
            data[changedIndex],
            previousData[changedIndex] as ProgramContentType
          );
          complete(changedIndex);
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (!isCoach && data && data.length > 0) {
      //액션 없이 다음 블록 열기
      if (
        lastIndex < data.length - 1 &&
        data[lastIndex].isShown &&
        !data[lastIndex].indentation &&
        (data[lastIndex].noArrow ||
          data[lastIndex].hideIfPatient ||
          data[lastIndex].isAlwaysHidden) &&
        !data[lastIndex].isEnd
      ) {
        complete(lastIndex);
      }
    }
  }, [data]);

  const [doneWithEnter, setDoneWithEnter] = useState<boolean>(false);

  useEffect(() => {
    function getShownNumber(data: ProgramContentType[]) {
      return (data as ProgramContentType[]).filter(
        (element) => element.isShown && !element.isHidden
      ).length;
    }

    if (
      !isCoach &&
      previousData &&
      data &&
      ((lastIndex === data.length - 1 && !isDone) ||
        (lastIndex < data.length - 1 &&
          (!data[lastIndex + 1].isShown || data[lastIndex + 1].isHidden))) &&
      !_.isEqual(previousData, data)
    ) {
      const prevShown = getShownNumber(previousData);
      const nowShown = getShownNumber(data);
      // Only auto-scroll when exactly one block was newly shown (likely user progression)
      if (prevShown > 0 && nowShown === prevShown + 1) {
        // Prevent unintended auto-scroll on initial open/hydration
        if (skipNextAutoScrollRef.current) {
          skipNextAutoScrollRef.current = false;
          return;
        }
        setMoveToIndex(lastIndex);
      }
    }
  }, [taskKey, data, previousData, isCoach, lastIndex, isDone]);

  const openNextBlock = useCallback(
    (index: number) => {
      if (!data) return index;
      const isLastBlock = index === data.length - 1;
      if (isLastBlock || data[index].isEnd) return index;

      const dataTemp = _.cloneDeep(data);
      const nextBlockIndex = dataTemp.findIndex(
        (element, blockIndex) =>
          blockIndex > index &&
          element.blockId &&
          element.blockId === dataTemp[index].nextBlockId
      );
      const targetIndex = nextBlockIndex > -1 ? nextBlockIndex : index + 1;
      dataTemp[targetIndex].isShown = true;
      dataTemp[targetIndex].isHidden = false;

      flushSync(() => {
        setData(dataTemp);
        setCurrentIndex(targetIndex);
      });

      return targetIndex;
    },
    [data]
  );

  const complete = useCallback(
    (index: number) => {
      (document.activeElement as HTMLElement)?.blur();
      if (
        data &&
        (data[index].isAlwaysHidden ||
          isBlockUserFieldFilled(data[index], data, user))
      ) {
        const nextIdx = openNextBlock(index);
        focusBlockInput(nextIdx);
      }
    },
    [data, isCoach, setData, device]
  );

  //엔터로 다음 블록 열기
  useEffect(() => {
    const goNext = (e: KeyboardEvent) => {
      if (
        !isMobile &&
        (e.code === "Enter" ||
          e.code === "NumpadEnter" ||
          e.key === "Enter" ||
          e.key === "NumpadEnter")
      ) {
        if (data && data[lastIndex] && !e.shiftKey) {
          e.preventDefault();
          complete(lastIndex);
        }
      }
    };
    if (!isCoach) {
      window.addEventListener("keypress", goNext);
      return () => window.removeEventListener("keypress", goNext);
    }
  }, [data, taskKey, complete, lastIndex, isCoach]);

  useEffect(() => {
    if (moveToIndex !== undefined && blockRefs.current[moveToIndex]) {
      smoothScrollTo(moveToIndex);
    }
  }, [moveToIndex, smoothScrollTo]);

  useEffect(() => {
    if (data && !containerHeight) {
      if (scrollRef.current && scrollRef.current?.offsetHeight > 0) {
        setContainerHeight(scrollRef.current?.offsetHeight);
      }
    }
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Sheet sx={{ backgroundColor: "transparent", height: "100%" }}>
        <Stack
          alignItems={"center"}
          justifyContent="center"
          sx={{ height: "100%" }}
        >
          <CircularProgress color="neutral" />
        </Stack>
      </Sheet>
    );
  }

  return (
    <>
      <CustomModal
        open={readOnlyModalOpen}
        onClose={() => setReadOnlyModalOpen(false)}
        title={"지금은 수정할 수 없어요"}
        subtitle={"내용을 수정하려면 ‘마침’을 눌러 활동지를 다시 열어주세요."}
        actions={[
          {
            label: "확인",
            onClick: () => setReadOnlyModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
      <div
        id="scrollContainer"
        ref={scrollRef}
        onScroll={() => {
          if (moveToIndex !== undefined) {
            setMoveToIndex(undefined);
          }
        }}
        className={`h-full w-full overflow-y-auto pt-[8px] px-[20px]`}
        style={{
          paddingBottom: (containerHeight || 72) / 2,
          overflowX: "hidden",
        }}
      >
        {data.map((blockData, index) => (
          <Block
            ref={(el) => (blockRefs.current[index] = el as HTMLDivElement)}
            key={`${taskKey}_${index}`}
            taskKey={taskKey}
            thoughtRecordKey={thoughtRecordKey}
            activityRecordKey={activityRecordKey}
            meditationRecordKey={meditationRecordKey}
            data={data}
            setData={setData}
            blockDataStr={JSON.stringify(blockData)}
            index={index}
            complete={complete}
            isCurrentIndex={lastIndex === index}
            doneWithEnter={doneWithEnter}
            setDoneWithEnter={setDoneWithEnter}
            setMoveToIndex={setMoveToIndex}
            isDone={isDone}
            isOpen={isOpen}
            isLastIndex={index === data.length - 1}
            isBeforeNoBorder={index >= 1 && data[index - 1].noBorder}
            isNextOpen={index < data.length - 1 && data[index + 1].isShown}
            translationVersion={translationVersion}
            viewerMode={viewerMode}
            onAttemptEdit={handleAttemptEdit}
          />
        ))}
      </div>
    </>
  );
}
